import { Box, Grid, IconButton } from '@mui/material';
import { ChangeUserPasswordRequest } from '@pxtra-client-lib-legacy/gen';
import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useStore } from '../../../../hooks/useStore';
import { ContentHead, Form, FormTextField, useForm } from '../../../../lib/components';
import { ContentBox } from '../../../../lib/components/compound/pages/contentBox';
import { EyeClosedIcon, EyeOpenIcon, SaveIcon } from '../../../../lib/icons';
import Util from '../../../../misc/util';
import { FormikValues } from 'formik';
import { ErrorObject } from '../../../../stores/authenticationStore';
import { useNavigate } from 'react-router';
import { Button } from '../../../../lib/components/atomic/button';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';

export const UserChangePassword: FC = observer(() => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { employeeStore, authenticationStore, companyStore } = useStore();
  const form = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<ErrorObject>({});
  const navigate = useNavigate();

  const passwordLoginDeactivated =
    companyStore.settings?.platform_single_sign_on_enabled &&
    !companyStore.settings?.platform_password_auth_enabled;

  const stopClick = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleSuccess = () => {
    document.addEventListener('click', stopClick, true);
    snackbar.enqueueSnackbar({
      variant: 'success',
      message: t('account:PASSWORD_HAS_BEEN_CHANGED')
    });
    setTimeout(() => {
      document.removeEventListener('click', stopClick, true);
      navigate('/dashboard');
    }, 2000);
  };

  const ShowPasswordButton = () => {
    return (
      <IconButton
        aria-label={t('auth:showPasswordHint')}
        onClick={() => setShowPassword((prev) => !prev)}
      >
        {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
      </IconButton>
    );
  };

  const validate = async (values: FormikValues) => {
    const errors = await authenticationStore.validatePassword(values);
    setErrors(errors);
  };

  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t('account:SET_NEW_PASSWORD')}
          showActions
          renderActions={() => (
            <Button onClick={form.handleSubmit} disabled={passwordLoginDeactivated}>
              <SaveIcon />
              {t('common:SAVE')}
            </Button>
          )}
        />
      }
    >
      <Box sx={{ '.MuiInput-root': { gap: 0 } }}>
        <Form
          initialValues={{
            old_password: '',
            new_password: '',
            confirm_new_password: ''
          }}
          formRef={form.formRef}
          validate={validate}
          onSubmit={async (values) => {
            employeeStore
              .changeUserPassword(values as ChangeUserPasswordRequest)
              .then(handleSuccess)
              .catch((e) => {
                if ([429].includes((e as { status: number }).status)) {
                  snackbar.enqueueSnackbar({
                    variant: 'error',
                    message: t('account:ERROR_RATE_LIMIT')
                  });
                } else {
                  let message = '';
                  if ('body' in e && e.body) {
                    message =
                      e.body.errors?.non_field_errors?.[0] ??
                      e.body.non_field_errors?.[0] ??
                      e.body.message ??
                      '';
                  }

                  snackbar.enqueueSnackbar({
                    variant: 'detailedError',
                    message: t('auth:passwordChangeError'),
                    details: message
                  });
                }
              });
          }}
          useCustomButton
        >
          <FormTextField
            type={showPassword ? 'text' : 'password'}
            label={t('account:OLD_PASSWORD')}
            placeholder={t('template:YOUR_PROP', {
              val: Util.lowerCaseFirstLetter(t('account:OLD_PASSWORD'))
            })}
            name="old_password"
            endAdornment={<ShowPasswordButton />}
            xs={6}
            errors={errors.old_password}
            disabled={passwordLoginDeactivated}
          />
          <FormTextField
            type={showPassword ? 'text' : 'password'}
            label={t('account:NEW_PASSWORD')}
            placeholder={t('template:YOUR_PROP', {
              val: Util.lowerCaseFirstLetter(t('account:NEW_PASSWORD'))
            })}
            name="new_password"
            endAdornment={<ShowPasswordButton />}
            xs={6}
            errors={errors.new_password}
            disabled={passwordLoginDeactivated}
          />
          <Grid item xs={6} />
          <FormTextField
            type={showPassword ? 'text' : 'password'}
            label={t('auth:repeatPassword')}
            placeholder={t('template:YOUR_PROP', {
              val: Util.lowerCaseFirstLetter(t('account:NEW_PASSWORD'))
            })}
            name="confirm_new_password"
            endAdornment={<ShowPasswordButton />}
            xs={6}
            errors={errors.confirm_new_password}
            disabled={passwordLoginDeactivated}
          />
        </Form>
      </Box>
      {passwordLoginDeactivated && (
        <Box sx={{ marginTop: '32px' }}>
          <FeedbackAlert
            title={t('common:HINTS')}
            description={
              t('company:PASSWORD_DEACTIVATED_HINTS', { returnObjects: true }) as string[]
            }
            severity="info"
          />
        </Box>
      )}
    </ContentBox>
  );
});
